// src/components/walkthrough/steps.js
//
// The first sign-in walkthrough's steps (docs/changes/onboarding-walkthrough/
// SPEC.md section 4): what each says, who sees it, where it plays and what it
// rings. The ids and their order are stepIds.json, which the stub and the live
// suite read as well, so a step added there is one nobody has seen anywhere.
//
// Every element a step rings carries a `data-tour` hook. A step's `find` hands
// back all of its elements or null, so the overlay never rings half a step -
// or NOTHING, when the page has answered and has nothing for the step to ring,
// which passes it over at once instead of after FIND_MS.

import STEP_IDS from "./stepIds.json";
import { NAV_ITEMS } from "../navItems";
import { brand, sentenceOf } from "../../config/brand";

export { STEP_IDS };

/** What a step's `find` returns when it has nothing to ring and never will. */
export const NOTHING = Object.freeze([]);

/** The selector for a `data-tour` hook. */
export const tourHook = (name) => `[data-tour="${name}"]`;

/* Every hook named, or null until they are all on the page. */
function every(root, names) {
  const found = names.map((name) => root.querySelector(tourHook(name)));
  return found.every(Boolean) ? found : null;
}

const hooked = (root, name) => [...root.querySelectorAll(tourHook(name))];

/* Who sees a step that stands for a nav link: the link's own rule. */
function navVisible(id, { canEdit }) {
  const item = NAV_ITEMS.find((nav) => nav.tour === id);
  return Boolean(item) && (!item.editorsOnly || canEdit);
}

/* A link and its Home tile on desktop; below 1280px the bar's links are only
   in the drawer, so the tile alone (SPEC decision 7). */
const navFind =
  (id, { desktop: extra = [], narrow = [`tile-${id}`] } = {}) =>
  ({ desktop, root }) =>
    every(root, desktop ? [`nav-${id}`, ...extra, `tile-${id}`] : narrow);

/* SPEC decision 8: the Role list of a user with no role if there is one,
   else the first row that is not the signed-in Admin's own. */
function userRoleList(root, sub) {
  const others = hooked(root, "user-role").filter(
    (el) => el.dataset.sub !== sub,
  );
  return others.find((el) => el.dataset.role === "") ?? others[0] ?? null;
}

const MULTI_ORGANIZATION = () => brand.organizations.length > 1;

/* Step 7's text. With Microsoft sign-in, staff on its domains cannot be
   added by hand (microsoft-sign-in SPEC decision 7); they appear after their
   first sign-in and are given a role. With more than one organization, 3.6b's
   checkboxes are part of that (SPEC section 4, and PLAN decision 7). */
function addUserText() {
  const microsoft = brand.auth?.microsoft;
  const domains = microsoft?.emailDomains ?? [];
  if (!microsoft || domains.length === 0) {
    return "Click Add User to create an account. Change a role from the user's row.";
  }
  const pick = MULTI_ORGANIZATION()
    ? "Pick their role and organizations."
    : "Pick their role.";
  return `Staff show up here after their first Microsoft sign-in. ${pick} Use Add User for anyone outside ${sentenceOf(domains)}.`;
}

const home = () => "/";
const never = () => false;

const DEFINITIONS = {
  organization: ({ organizations }) => ({
    visible: organizations.length > 1,
    title: `Switch between ${sentenceOf(organizations.map((o) => o.shortName))}`,
    text: "Click a name to switch. Every screen then shows only that company's work.",
    path: home,
    drawer: ({ desktop }) => !desktop,
    find: ({ desktop, root }) =>
      every(root, [
        desktop ? "organization-switch" : "drawer-organization-switch",
      ]),
  }),

  companies: (who) => ({
    visible: navVisible("companies", who),
    title: "Companies",
    text: who.canEdit
      ? "Create a company, or open one to see its jobs, contacts and locations."
      : "Open a company to see its jobs, contacts and locations.",
    path: home,
    drawer: never,
    find: navFind("companies"),
  }),

  jobs: (who) => ({
    visible: navVisible("jobs", who),
    title: "Jobs and job search",
    text: who.canEdit
      ? "View all jobs, or search by Job Ticket ID. Create a job before adding warehouse inventory."
      : "View all jobs, or search by Job Ticket ID to open one.",
    path: home,
    drawer: never,
    // Below 1280px the bar's search is in the drawer; Home has its own.
    find: navFind("jobs", {
      desktop: ["bar-search"],
      narrow: ["home-search", "tile-jobs"],
    }),
  }),

  warehouse: (who) => ({
    visible: navVisible("warehouse", who),
    title: "Warehouse",
    text: "Enter tags or trailers for each job, view inventory lists, create BOLs, and share inventory links with customers.",
    path: home,
    drawer: never,
    find: navFind("warehouse"),
  }),

  "storage-billing": (who) => ({
    visible: navVisible("storage-billing", who),
    title: "Storage Billing",
    text: "Generate storage cost reports to send to customers.",
    path: home,
    drawer: never,
    find: navFind("storage-billing"),
  }),

  // Below 1280px the Admin button is in the drawer, so the step opens it there
  // and stays where it is (SPEC decision 7); User Management is next anyway.
  admin: ({ isAdmin }) => ({
    visible: isAdmin,
    title: "Admin",
    text: "Your admin tools. Manage Portal Users is where you add and remove users.",
    path: ({ desktop }) => (desktop ? "/AdminPage" : "/"),
    drawer: ({ desktop }) => !desktop,
    find: ({ desktop, root }) =>
      every(
        root,
        desktop ? ["admin-button", "manage-users"] : ["drawer-admin"],
      ),
  }),

  "add-user": ({ isAdmin }) => ({
    visible: isAdmin,
    title: "Add a user",
    text: addUserText(),
    path: () => "/UserManagement",
    drawer: never,
    // On a phone the row is a screen away from Add User and its cells scroll
    // sideways, so the Role list alone; the text still names Add User.
    find: ({ root, sub, phone }) => {
      const role = userRoleList(root, sub);
      const add = root.querySelector(tourHook("add-user"));
      // An Admin alone in the list - every new customer's first - has no row
      // to show, so Add User alone, once the rows have arrived. Waiting for
      // another row held the card on the step before for FIND_MS.
      if (!role) return add && hooked(root, "user-role").length ? [add] : null;
      if (phone) return [role];
      if (!add) return null;
      const boxes = MULTI_ORGANIZATION()
        ? hooked(root, "user-organizations").find(
            (el) => el.dataset.sub === role.dataset.sub,
          )
        : null;
      return boxes ? [role, boxes, add] : [role, add];
    },
  }),

  "remove-user": ({ isAdmin }) => ({
    visible: isAdmin,
    title: "Remove a user",
    text: "Click Deactivate to remove access. Nothing is deleted, and Reactivate undoes it.",
    path: () => "/UserManagement",
    drawer: never,
    // Never the Admin's own: nobody should be invited to deactivate themselves.
    // With the rows there and none to offer, there is no step to show.
    find: ({ root, sub }) => {
      const rows = hooked(root, "user-status");
      const button = rows.find(
        (el) => el.dataset.sub !== sub && el.dataset.enabled === "true",
      );
      if (button) return [button];
      return rows.length ? NOTHING : null;
    },
  }),

  // Last for everyone, so the tour ends on where to find it again
  // (help-module SPEC section 4). Below 1280px the bar's icon is gone and
  // Home's link stands alone, without opening the drawer.
  help: () => ({
    visible: true,
    title: "Help",
    text: "Replay this tour any time from Help.",
    path: home,
    drawer: never,
    find: ({ desktop, root }) =>
      every(root, desktop ? ["bar-help", "home-help"] : ["home-help"]),
  }),
};

/**
 * The steps of one person's version, in order. `who` is `{ isAdmin, canEdit,
 * organizations }`, where `organizations` is the ones they may use - with one,
 * there is no switch and so no step 1, whichever reason left them one.
 */
export function stepsFor(who) {
  return STEP_IDS.map((id) => ({ id, ...DEFINITIONS[id](who) })).filter(
    (step) => step.visible,
  );
}

/** For the test that holds stepIds.json and this file to each other. */
export const DEFINED_IDS = Object.keys(DEFINITIONS);
